import { useEffect, useState } from "react"
import { FileText, Mail, Download, Send, Upload, Trash2, Star, Globe, EyeOff } from "lucide-react"
import { useParams } from "react-router-dom"
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Tabs, TabsContent, TabsList, TabsTrigger } from "../ui"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog"
import { Textarea } from "../ui/textarea"
import { Checkbox } from "../ui/checkbox"
import { getProperty, emailPropertyDocuments, uploadPropertyDocumentsByCategory, deletePropertyDocument, setPrimaryPropertyDocument, setPropertyDocumentOnline } from "../../api/agent"
import toast from "react-hot-toast"

// Single source of truth for document categories.
// `value` matches the backend PropertyDocument category; `label` is shown in the UI.
// `selectable` controls whether agents can choose it when uploading (legacy "Legal"
// is kept for display/back-compat but is no longer offered as a new upload type).
const DOC_CATEGORIES = [
  { value: "statement_of_info_docs", label: "Property Price Statement", selectable: true },
  { value: "contract_docs", label: "Contract", selectable: true },
  { value: "lease_docs", label: "Lease", selectable: true },
  { value: "property_report_docs", label: "Property Report", selectable: true },
  { value: "legal_docs", label: "Legal", selectable: false },
  { value: "other_docs", label: "Other", selectable: true },
]

const CATEGORY_LABELS = DOC_CATEGORIES.reduce((acc, c) => {
  acc[c.value] = c.label
  return acc
}, {})

const EMPTY_GROUPS = DOC_CATEGORIES.reduce((acc, c) => {
  acc[c.value] = []
  return acc
}, {})

const groupDocuments = (docs = []) => {
  const groups = DOC_CATEGORIES.reduce((acc, c) => {
    acc[c.value] = []
    return acc
  }, {})
  docs.forEach((d) => {
    const cat = d?.category
    if (groups[cat]) {
      groups[cat].push(d)
    } else {
      // Unknown/legacy category -> bucket under "Other" so it stays visible.
      groups.other_docs.push(d)
    }
  })
  return groups
}

const formatDate = (iso) => {
  if (!iso) return "-"
  try {
    return new Date(iso).toLocaleDateString()
  } catch (e) {
    return iso
  }
}

export default function PropertyDocumentsPage() {
  const params = useParams()
  const [address, setAddress] = useState("")
  const [defaultToEmail, setDefaultToEmail] = useState("")
  const [isLease, setIsLease] = useState(false)
  const [documents, setDocuments] = useState(EMPTY_GROUPS)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showEmailDialog, setShowEmailDialog] = useState(false)
  const [emailSelectedDocs, setEmailSelectedDocs] = useState([])
  const [emailSelectAll, setEmailSelectAll] = useState(false)
  const [emailTo, setEmailTo] = useState("")
  const [emailSubject, setEmailSubject] = useState("")
  const [emailMessage, setEmailMessage] = useState("")
  const [isSending, setIsSending] = useState(false)
  // Single-document email dialog state
  const [showSingleEmailDialog, setShowSingleEmailDialog] = useState(false)
  const [singleDoc, setSingleDoc] = useState(null)
  const [singleToEmail, setSingleToEmail] = useState("")
  const [singleSubject, setSingleSubject] = useState("")
  const [singleMessage, setSingleMessage] = useState("")
  const [isSendingSingle, setIsSendingSingle] = useState(false)

  // Upload modal state
  const [showUploadDialog, setShowUploadDialog] = useState(false)
  // Each pending upload: { file, category }. Default category to "Other" so a
  // file is always valid to upload, but the agent can change it per file.
  const [pendingUploads, setPendingUploads] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)

  // Property Price Statement is a sale-only compliance document; rentals
  // default new uploads to Lease instead.
  const defaultUploadCategory = isLease ? "lease_docs" : "statement_of_info_docs"

  const addPendingFiles = (files) => {
    const items = Array.from(files || []).map((file) => ({
      file,
      category: defaultUploadCategory,
    }))
    if (items.length) {
      setPendingUploads((prev) => [...prev, ...items])
    }
  }

  const resetUploadState = () => {
    setPendingUploads([])
    setIsDragging(false)
  }

  // Delete state
  const [docToDelete, setDocToDelete] = useState(null)
  const [settingPrimaryId, setSettingPrimaryId] = useState(null)
  const [togglingOnlineId, setTogglingOnlineId] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    let isMounted = true
    setIsLoading(true)
    getProperty(params.id)
      .then((res) => {
        if (!isMounted) return
        const data = res?.data
        setAddress(data?.address || "")
        setIsLease(data?.transaction_type === 'lease')
        // default recipient preference: owner email then agent email
        const to = data?.owner?.email || data?.agent?.email || ""
        setDefaultToEmail(to)
        setDocuments(groupDocuments(data?.documents || []))
      })
      .catch((err) => {
        if (!isMounted) return
        setError(err?.response?.data?.detail || "Failed to load documents")
      })
      .finally(() => {
        if (!isMounted) return
        setIsLoading(false)
      })
    return () => { isMounted = false }
  }, [params.id])

  const allDocuments = DOC_CATEGORIES.flatMap((c) => documents[c.value] || [])

  const handleEmailSelectDocument = (docId) => {
    setEmailSelectedDocs((prev) => (prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]))
  }

  const handleEmailSelectAll = () => {
    if (emailSelectAll) {
      setEmailSelectedDocs([])
    } else {
      setEmailSelectedDocs(allDocuments.map((doc) => doc.id))
    }
    setEmailSelectAll(!emailSelectAll)
  }

  const handleEmailDialogOpen = (open) => {
    setShowEmailDialog(open)
    if (open) {
      setEmailSelectedDocs([])
      setEmailSelectAll(false)
      setEmailTo("")
      setEmailSubject("")
      setEmailMessage("")
    }
  }
  const openSingleEmailDialog = (doc) => {
    setSingleDoc(doc)
    setSingleToEmail(defaultToEmail || "")
    setSingleSubject(`Document: ${doc?.name || "Property Document"}`)
    setSingleMessage("")
    setShowSingleEmailDialog(true)
  }
  const handleSendSingleEmail = async () => {
    if (!singleDoc?.id) return
    if (!singleToEmail?.trim()) return
    try {
      setIsSendingSingle(true)
      await emailPropertyDocuments(params.id, {
        document_ids: [singleDoc.id],
        to_email: singleToEmail.trim(),
        subject: singleSubject?.trim() || `Document: ${singleDoc?.name || "Property Document"}`,
        message: singleMessage?.trim() || "",
      })
      setShowSingleEmailDialog(false)
    } catch (e) {
      console.error(e)
    } finally {
      setIsSendingSingle(false)
    }
  }

  const handleSendEmail = async () => {
    if (!emailSelectedDocs.length) return
    if (!emailTo?.trim()) return
    try {
      setIsSending(true)
      await emailPropertyDocuments(params.id, {
        document_ids: emailSelectedDocs,
        to_email: emailTo.trim(),
        subject: emailSubject?.trim() || `Documents for ${address}`,
        message: emailMessage?.trim() || "",
      })
      setShowEmailDialog(false)
    } catch (e) {
      // Optionally surface error via toast
      console.error(e)
    } finally {
      setIsSending(false)
    }
  }
  const handleDownload = (doc) => {
    if (!doc?.file) return
    // fetch(doc.file) + blob used to live here, but doc.file is an S3 URL on a
    // different origin - a JS-initiated cross-origin fetch hits the bucket's
    // CORS policy and fails outright ("won't let me download the contract").
    // A plain navigation isn't subject to CORS at all, and it's the same
    // approach the Property Price Statement download already uses without issue.
    const link = document.createElement("a")
    link.href = doc.file
    link.target = "_blank"
    link.rel = "noopener noreferrer"
    // Best-effort filename hint - browsers only honour `download` for
    // same-origin links, so a cross-origin S3 URL just opens in a new tab
    // instead, which is still a successful download from the agent's POV.
    if (doc?.name) link.download = doc.name
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
    
  const handleDownloadAll = () => {
    if (!allDocuments.length) return
    
    // Show a toast notification about the download process
    toast.success(`Starting download of ${allDocuments.length} documents...`);
    
    // Trigger downloads for all docs with a small delay to avoid overwhelming the browser
    allDocuments.forEach((doc, index) => {
      setTimeout(() => {
        handleDownload(doc);
      }, index * 500); // 500ms delay between each download
    });
  }

  const handleDeleteDocument = async () => {
    if (!docToDelete?.id) return
    try {
      setIsDeleting(true)
      await deletePropertyDocument(docToDelete.id)
      setDocuments((prev) => {
        const updated = {}
        for (const [key, docs] of Object.entries(prev)) {
          updated[key] = docs.filter((d) => d.id !== docToDelete.id)
        }
        return updated
      })
      toast.success("Document deleted")
      setDocToDelete(null)
    } catch (e) {
      toast.error("Failed to delete document")
      console.error(e)
    } finally {
      setIsDeleting(false)
    }
  }

  // Mark one PPS/Statement as the main doc that feeds REA & Domain. This is
  // what agents should use instead of deleting an unwanted statement: it moves
  // the "Main" marker without removing anything (Monday 13075070528).
  const handleSetPrimary = async (doc) => {
    try {
      setSettingPrimaryId(doc.id)
      await setPrimaryPropertyDocument(doc.id)
      setDocuments((prev) => {
        const updated = {}
        for (const [key, docs] of Object.entries(prev)) {
          updated[key] = docs.map((d) =>
            key === "statement_of_info_docs"
              ? { ...d, is_primary: d.id === doc.id }
              : d
          )
        }
        return updated
      })
      toast.success("Main PPS updated — now on REA, Domain & the portal")
    } catch (e) {
      toast.error("Could not set the main PPS")
      console.error(e)
    } finally {
      setSettingPrimaryId(null)
    }
  }

  // Show or hide a non-PPS document on the buyer portal and public website.
  // Off by default, so old/duplicate versions stay private until an agent
  // opts them in (Monday 13213318951). The PPS shown online is governed by
  // "Set as main" above, so it has no separate toggle.
  const handleToggleOnline = async (doc) => {
    const next = !doc.show_online
    try {
      setTogglingOnlineId(doc.id)
      await setPropertyDocumentOnline(doc.id, next)
      setDocuments((prev) => {
        const updated = {}
        for (const [key, docs] of Object.entries(prev)) {
          updated[key] = docs.map((d) =>
            d.id === doc.id ? { ...d, show_online: next } : d
          )
        }
        return updated
      })
      toast.success(next ? "Now shown online" : "Hidden from online")
    } catch (e) {
      toast.error("Could not update online visibility")
      console.error(e)
    } finally {
      setTogglingOnlineId(null)
    }
  }

  const DocumentList = ({ documents, category, categoryValue, onEmail }) => (
    <div className="space-y-3">
      {documents.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No {category} documents uploaded yet</p>
         
        </div>
      ) : (
        documents.map((doc) => (
          <div
            key={doc.id}
            className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 bg-white border rounded-lg hover:shadow-sm transition-shadow"
          >
            <div className="flex items-center space-x-3 w-full min-w-0 sm:w-auto">
              <FileText className="w-5 h-5 text-slate-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 flex flex-wrap items-center gap-2 break-all">
                  {doc.name}
                  {categoryValue === "statement_of_info_docs" && doc.is_primary && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold px-2 py-0.5">
                      <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> Main — on REA, Domain &amp; Portal
                    </span>
                  )}
                  {categoryValue !== "statement_of_info_docs" && categoryValue !== "legal_docs" && doc.show_online && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-semibold px-2 py-0.5">
                      <Globe className="w-3 h-3" /> Shown online
                    </span>
                  )}
                </p>
                <p className="text-xs text-slate-500">
                  Uploaded {formatDate(doc.uploaded_at)}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {categoryValue === "statement_of_info_docs" && !doc.is_primary && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50"
                  disabled={settingPrimaryId === doc.id}
                  onClick={() => handleSetPrimary(doc)}
                  title="Publish this statement to REA & Domain"
                >
                  <Star className="w-4 h-4 mr-1" />
                  {settingPrimaryId === doc.id ? "Setting…" : "Set as main"}
                </Button>
              )}
              {categoryValue !== "statement_of_info_docs" && categoryValue !== "legal_docs" && (
                <Button
                  variant="outline"
                  size="sm"
                  className={doc.show_online
                    ? "text-slate-600 border-slate-300 hover:bg-slate-50"
                    : "text-emerald-700 border-emerald-300 hover:bg-emerald-50"}
                  disabled={togglingOnlineId === doc.id}
                  onClick={() => handleToggleOnline(doc)}
                  title={doc.show_online
                    ? "Hide this document from the portal & website"
                    : "Show this document on the portal & website"}
                >
                  {doc.show_online
                    ? <EyeOff className="w-4 h-4 mr-1" />
                    : <Globe className="w-4 h-4 mr-1" />}
                  {togglingOnlineId === doc.id
                    ? "Saving…"
                    : (doc.show_online ? "Hide online" : "Show online")}
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)}>
                <Download className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEmail?.(doc)}>
                <Mail className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => setDocToDelete(doc)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))
      )}
    </div>
  )

  return (
    <div className=" bg-slate-50">
        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-slate-600">Manage and share property documents</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Documents
                    </Button>
                  </DialogTrigger>
                </Dialog>

                <Dialog open={showEmailDialog} onOpenChange={handleEmailDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-teal-600 hover:bg-teal-700">
                      <Mail className="w-4 h-4 mr-2" />
                      Email Documents
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Email Documents</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6">
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <Label className="text-base font-medium">Select Documents to Email</Label>
                          <div className="flex items-center space-x-2">
                            <Checkbox checked={emailSelectAll} onCheckedChange={handleEmailSelectAll} />
                            <span className="text-sm">Select All</span>
                            {emailSelectedDocs.length > 0 && (
                              <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                                {emailSelectedDocs.length} selected
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="max-h-60 overflow-y-auto border rounded-lg p-4 space-y-2">
                          {allDocuments.map((doc) => (
                            <div key={doc.id} className="flex items-center space-x-3 p-2 hover:bg-slate-50 rounded">
                              <Checkbox
                                checked={emailSelectedDocs.includes(doc.id)}
                                onCheckedChange={() => handleEmailSelectDocument(doc.id)}
                              />
                              <FileText className="w-4 h-4 text-slate-500" />
                              <div className="flex-1">
                                <p className="text-sm font-medium">{doc.name}</p>
                                <p className="text-xs text-slate-500">{formatDate(doc.uploaded_at)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="email-to">Send To</Label>
                        <Input
                          id="email-to"
                          placeholder="Enter email address"
                          value={emailTo}
                          onChange={(e) => setEmailTo(e.target.value)}
                        />
                      </div>

                      <div>
                        <Label htmlFor="email-subject">Subject</Label>
                        <Input
                          id="email-subject"
                          placeholder={`Documents for ${address}`}
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                        />
                      </div>

                      <div>
                        <Label htmlFor="email-message">Message (Optional)</Label>
                        <Textarea
                          id="email-message"
                          placeholder="Please find attached the requested property documents..."
                          rows={3}
                          value={emailMessage}
                          onChange={(e) => setEmailMessage(e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 mt-6">
                      <Button variant="outline" onClick={() => setShowEmailDialog(false)}>
                        Cancel
                      </Button>
                      <Button
                        disabled={emailSelectedDocs.length === 0 || !emailTo?.trim() || isSending}
                        className="bg-teal-600 hover:bg-teal-700"
                        onClick={handleSendEmail}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {isSending ? "Sending..." : `Send Email (${emailSelectedDocs.length})`}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <Button variant="outline" disabled={allDocuments.length === 0} onClick={handleDownloadAll}>
                  <Download className="w-4 h-4 mr-2" />
                  Download All
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documents Tabs */}
        <Card>
          <CardHeader>
            <CardTitle>All Documents</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-slate-500">Loading documents...</div>
            ) : error ? (
              <div className="text-red-600">{error}</div>
            ) : (
              (() => {
                // Show selectable categories always; legacy categories (e.g. Legal)
                // only when they actually contain documents. Statement of Info is
                // sale-only, so hide it on rentals unless a legacy doc is already
                // filed there.
                const visible = DOC_CATEGORIES.filter((c) => {
                  const hasDocs = (documents[c.value] || []).length > 0
                  if (isLease && c.value === 'statement_of_info_docs') return hasDocs
                  return c.selectable || hasDocs
                })
                return (
                  <Tabs defaultValue={visible[0]?.value} className="w-full">
                    <TabsList
                      className="w-full flex-wrap gap-1 !h-auto !justify-start sm:grid sm:!h-10"
                      style={{ gridTemplateColumns: `repeat(${visible.length}, minmax(0, 1fr))` }}
                    >
                      {visible.map((c) => (
                        <TabsTrigger key={c.value} value={c.value}>
                          {c.label} ({(documents[c.value] || []).length})
                        </TabsTrigger>
                      ))}
                    </TabsList>

                    {visible.map((c) => (
                      <TabsContent key={c.value} value={c.value} className="mt-6">
                        <DocumentList
                          documents={documents[c.value] || []}
                          category={c.label}
                          categoryValue={c.value}
                          onEmail={openSingleEmailDialog}
                        />
                      </TabsContent>
                    ))}
                  </Tabs>
                )
              })()
            )}
          </CardContent>
        </Card>
        {/* Single Document Email Dialog */}
        <Dialog open={showSingleEmailDialog} onOpenChange={setShowSingleEmailDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Email Document</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="text-sm text-slate-700">
                <span className="font-medium">Document:</span> {singleDoc?.name || "-"}
              </div>
              <div>
                <Label htmlFor="single-to">Recipient</Label>
                <Input
                  id="single-to"
                  placeholder="Enter recipient email"
                  value={singleToEmail}
                  onChange={(e) => setSingleToEmail(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="single-subject">Subject</Label>
                <Input
                  id="single-subject"
                  placeholder={`Document: ${singleDoc?.name || "Property Document"}`}
                  value={singleSubject}
                  onChange={(e) => setSingleSubject(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="single-message">Message (Optional)</Label>
                <Textarea
                  id="single-message"
                  placeholder="Please find the requested document attached."
                  rows={3}
                  value={singleMessage}
                  onChange={(e) => setSingleMessage(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowSingleEmailDialog(false)}>Cancel</Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700"
                disabled={!singleToEmail?.trim() || isSendingSingle}
                onClick={handleSendSingleEmail}
              >
                {isSendingSingle ? "Sending..." : "Send"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!docToDelete} onOpenChange={(open) => { if (!open) setDocToDelete(null) }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete Document</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              Are you sure you want to delete <span className="font-medium">{docToDelete?.name}</span>? This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setDocToDelete(null)} disabled={isDeleting}>Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700" onClick={handleDeleteDocument} disabled={isDeleting}>
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Upload Documents Dialog */}
        <Dialog
          open={showUploadDialog}
          onOpenChange={(open) => {
            setShowUploadDialog(open);
            if (!open) resetUploadState();
          }}
        >
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Upload Documents</DialogTitle>
            </DialogHeader>

            {/* min-w-0 is required here: DialogContent is display:grid, so this
                direct grid item defaults to min-width:auto - its automatic minimum
                size is the longest unbroken descendant (the truncate/nowrap
                filename below), which silently overrides that span's own
                truncate/min-w-0 and forces the whole dialog to grow instead of
                ellipsizing. Without this, a long filename still blows out the box. */}
            <div className="space-y-6 min-w-0">
              {/* Single drop zone — drag files in, then pick what each one is below */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  addPendingFiles(e.dataTransfer.files);
                }}
                className={`border-2 border-dashed rounded-lg p-10 text-center transition-colors ${
                  isDragging ? "border-blue-500 bg-blue-50" : "border-slate-300"
                }`}
              >
                <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? "text-blue-500" : "text-slate-400"}`} />
                <p className="text-slate-700 font-medium mb-1">
                  Drag &amp; drop documents here
                </p>
                <p className="text-sm text-slate-500 mb-4">
                  Drop any files, then choose what each one is below.
                </p>
                <label className="inline-flex items-center justify-center px-4 py-2 border rounded-md text-sm font-medium bg-white text-slate-700 border-slate-300 hover:bg-slate-50 cursor-pointer">
                  <Upload className="w-4 h-4 mr-2" />
                  Browse Files
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      addPendingFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>

              {/* Pending files — each gets a document-type selector */}
              {pendingUploads.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-slate-700">
                    {pendingUploads.length} file{pendingUploads.length > 1 ? "s" : ""} ready — set the type for each
                  </Label>
                  {pendingUploads.map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg min-w-0"
                    >
                      <FileText className="w-5 h-5 text-slate-500 shrink-0" />
                      <span className="text-sm text-slate-700 flex-1 truncate min-w-0" title={item.file.name}>
                        {item.file.name}
                      </span>
                      <select
                        value={item.category}
                        onChange={(e) => {
                          const val = e.target.value;
                          setPendingUploads((prev) =>
                            prev.map((it, idx) => (idx === i ? { ...it, category: val } : it))
                          );
                        }}
                        className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500 w-36 shrink-0 truncate"
                      >
                        {DOC_CATEGORIES.filter(
                          (c) => c.selectable && !(isLease && c.value === 'statement_of_info_docs')
                        ).map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="text-slate-500 hover:text-red-600 text-sm shrink-0"
                        onClick={() =>
                          setPendingUploads((prev) => prev.filter((_, idx) => idx !== i))
                        }
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-between mt-6 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowUploadDialog(false);
                    resetUploadState();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={async () => {
                    if (pendingUploads.length === 0) {
                      toast.error("Please add at least one file");
                      return;
                    }
                    try {
                      setIsUploading(true);
                      await uploadPropertyDocumentsByCategory(params.id, pendingUploads);
                      toast.success("Documents uploaded successfully!");
                      // Refresh the documents list
                      const res = await getProperty(params.id);
                      const data = res?.data;
                      setDocuments(groupDocuments(data?.documents || []));
                      setShowUploadDialog(false);
                      resetUploadState();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Upload failed");
                    } finally {
                      setIsUploading(false);
                    }
                  }}
                  disabled={isUploading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {isUploading ? "Uploading..." : `Upload ${pendingUploads.length || ""} Document${pendingUploads.length === 1 ? "" : "s"}`.trim()}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
    </div>
  )
}
